import apiClient from '../../config/api';

export interface MicPairingCreated {
  pairingId: string;
  token: string;
  expiresAt: string;
  joinUrl: string;
  code: string;
}

/** Extrae el código de `/mic/:code` por si el backend no manda `code`. */
export function pairingCodeFromJoinUrl(joinUrl: string): string | undefined {
  try {
    const path = new URL(joinUrl).pathname;
    const match = path.match(/\/mic\/([^/]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reancla el `joinUrl` al origen desde el que se está usando la aplicación.
 *
 * El backend lo construye con un único `FRONTEND_URL` por entorno, sin mirar la
 * marca. En una marca blanca eso manda el teléfono al dominio de OTRA marca:
 * el móvil ve el logo equivocado y acaba en un host distinto al del
 * escritorio. El QR tiene que apuntar al mismo origen que el navegador que lo
 * genera, y eso es exactamente `location.origin` — no hay forma de que se
 * equivoque, no depende de que el catálogo de marcas esté bien sembrado.
 *
 * Se sustituye SOLO el origen: la ruta, el código y el token se conservan tal
 * y como los emitió el backend.
 *
 * Efecto colateral útil en local: abriendo Vite por la IP de la LAN, el QR deja
 * de apuntar a `localhost`, que en un móvil no resuelve a nada.
 */
export function joinUrlForCurrentOrigin(
  joinUrl: string,
  origin: string = window.location.origin,
): string {
  try {
    const parsed = new URL(joinUrl);
    return new URL(`${parsed.pathname}${parsed.search}`, origin).toString();
  } catch {
    return joinUrl;
  }
}

class MicPairingService {
  async create(draftId?: string): Promise<MicPairingCreated> {
    const response = await apiClient.post<MicPairingCreated>(
      '/mic-pairing',
      draftId ? { draftId } : {},
    );
    const data = response.data;
    return {
      ...data,
      code: data.code || pairingCodeFromJoinUrl(data.joinUrl) || '',
    };
  }

  async revoke(pairingId: string): Promise<void> {
    await apiClient.delete(`/mic-pairing/${pairingId}`);
  }
}

export const micPairingService = new MicPairingService();
